import React, { useState } from 'react';
import { Film, Sparkles, Heart, Star, ArrowRight, Check, X, Mail, Lock, User, Eye, EyeOff, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';
import Toast from '../components/Toast';

const LandingPage = ({ onGetStarted }) => {
    const { t } = useLanguage();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [isLogin, setIsLogin] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const { login, register } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        name: '',
        confirmPassword: '',
        birthDate: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [toast, setToast] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.email) {
            newErrors.email = t('auth.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = t('auth.emailInvalid');
        }
        if (!formData.password) {
            newErrors.password = t('auth.passwordRequired');
        } else if (!isLogin) {
            // Sadece kayıt olurken güçlü şifre kontrolü yap
            if (formData.password.length < 6) {
                newErrors.password = t('auth.passwordMinLength');
            } else if (!/[A-Z]/.test(formData.password)) {
                newErrors.password = t('auth.passwordUpperCase');
            } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
                newErrors.password = t('auth.passwordSpecialChar');
            }
        }
        if (!isLogin) {
            if (!formData.name) {
                newErrors.name = t('auth.nameRequired');
            }
            if (!formData.birthDate) {
                newErrors.birthDate = t('auth.birthDateRequired');
            } else {
                // 18 yaş kontrolü
                const birthDate = new Date(formData.birthDate);
                const today = new Date();
                const age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                    if (age - 1 < 18) {
                        newErrors.birthDate = t('auth.ageRestriction');
                    }
                } else if (age < 18) {
                    newErrors.birthDate = t('auth.ageRestriction');
                }
            }
            if (!formData.confirmPassword) {
                newErrors.confirmPassword = t('auth.confirmPasswordRequired');
            } else if (formData.password !== formData.confirmPassword) {
                newErrors.confirmPassword = t('auth.passwordMismatch');
            }
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        setErrors({});

        try {
            // Email'i normalize et (trim ve lowercase)
            const normalizedEmail = formData.email.trim().toLowerCase();

            if (isLogin) {
                await login(normalizedEmail, formData.password);
                // Başarılı giriş - tik göster ve modal'ı kapat
                setShowSuccess(true);
                setTimeout(() => {
                    setShowAuthModal(false);
                    setShowSuccess(false);
                }, 1500);
            } else {
                const result = await register(normalizedEmail, formData.password, formData.name, formData.birthDate);
                // E-posta doğrulama gerekiyorsa bilgi göster
                if (result?.needsEmailVerification) {
                    // Form'u temizle ve login moduna geç
                    setFormData({
                        email: '',
                        password: '',
                        name: '',
                        confirmPassword: '',
                        birthDate: ''
                    });
                    setErrors({});
                    setIsLogin(true); // Login moduna geç

                    setToast({
                        message: result.message,
                        type: 'info'
                    });
                } else {
                    // Başarılı kayıt - form'u temizle, login moduna geç, tik göster ve modal'ı kapat
                    setFormData({
                        email: '',
                        password: '',
                        name: '',
                        confirmPassword: '',
                        birthDate: ''
                    });
                    setErrors({});
                    setIsLogin(true); // Login moduna geç

                    setShowSuccess(true);
                    setTimeout(() => {
                        setShowAuthModal(false);
                        setShowSuccess(false);
                    }, 1500);
                }
            }
        } catch (error) {
            // Hata mesajını toast ile göster ve modal açık kalsın
            const errorMessage = error.message || t('auth.error');
            setToast({
                message: errorMessage,
                type: 'error'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleAuthMode = () => {
        setIsLogin(!isLogin);
        setFormData({
            email: '',
            password: '',
            name: '',
            confirmPassword: '',
            birthDate: ''
        });
        setErrors({});
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-purple-900 dark:to-indigo-900 relative overflow-hidden">
            {/* Dekoratif Dalgalı Çizgiler - Sol Taraf */}
            <div className="fixed left-0 top-0 bottom-0 w-48 pointer-events-none hidden lg:block z-0">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 1000" preserveAspectRatio="none">
                    {/* Ana dalgalı çizgi - kalın */}
                    <path
                        d="M 0,0 Q 80,100 0,200 T 0,400 T 0,600 T 0,800 T 0,1000"
                        stroke="url(#gradient-wave-left-1)"
                        strokeWidth="6"
                        fill="none"
                        opacity="0.3"
                        strokeLinecap="round"
                    />
                    {/* İkincil dalga */}
                    <path
                        d="M 40,0 Q 100,120 40,240 T 40,480 T 40,720 T 40,960 L 40,1000"
                        stroke="url(#gradient-wave-left-2)"
                        strokeWidth="4"
                        fill="none"
                        opacity="0.25"
                        strokeLinecap="round"
                    />
                    {/* İnce tamamlayıcı */}
                    <path
                        d="M 20,50 Q 90,150 20,250 T 20,450 T 20,650 T 20,850"
                        stroke="url(#gradient-wave-left-3)"
                        strokeWidth="2"
                        fill="none"
                        opacity="0.2"
                        strokeLinecap="round"
                    />
                    <defs>
                        <linearGradient id="gradient-wave-left-1" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#9333ea" />
                            <stop offset="50%" stopColor="#6366f1" />
                            <stop offset="100%" stopColor="#8b5cf6" />
                        </linearGradient>
                        <linearGradient id="gradient-wave-left-2" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#ec4899" />
                            <stop offset="50%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#6366f1" />
                        </linearGradient>
                        <linearGradient id="gradient-wave-left-3" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#3b82f6" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* Dekoratif Dalgalı Çizgiler - Sağ Taraf */}
            <div className="fixed right-0 top-0 bottom-0 w-48 pointer-events-none hidden lg:block z-0">
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 1000" preserveAspectRatio="none">
                    {/* Ana dalgalı çizgi - kalın */}
                    <path
                        d="M 200,0 Q 120,100 200,200 T 200,400 T 200,600 T 200,800 T 200,1000"
                        stroke="url(#gradient-wave-right-1)"
                        strokeWidth="6"
                        fill="none"
                        opacity="0.3"
                        strokeLinecap="round"
                    />
                    {/* İkincil dalga */}
                    <path
                        d="M 160,0 Q 100,120 160,240 T 160,480 T 160,720 T 160,960 L 160,1000"
                        stroke="url(#gradient-wave-right-2)"
                        strokeWidth="4"
                        fill="none"
                        opacity="0.25"
                        strokeLinecap="round"
                    />
                    {/* İnce tamamlayıcı */}
                    <path
                        d="M 180,50 Q 110,150 180,250 T 180,450 T 180,650 T 180,850"
                        stroke="url(#gradient-wave-right-3)"
                        strokeWidth="2"
                        fill="none"
                        opacity="0.2"
                        strokeLinecap="round"
                    />
                    <defs>
                        <linearGradient id="gradient-wave-right-1" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#6366f1" />
                            <stop offset="50%" stopColor="#8b5cf6" />
                            <stop offset="100%" stopColor="#9333ea" />
                        </linearGradient>
                        <linearGradient id="gradient-wave-right-2" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#a855f7" />
                            <stop offset="50%" stopColor="#ec4899" />
                            <stop offset="100%" stopColor="#6366f1" />
                        </linearGradient>
                        <linearGradient id="gradient-wave-right-3" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stopColor="#8b5cf6" />
                            <stop offset="50%" stopColor="#3b82f6" />
                            <stop offset="100%" stopColor="#ec4899" />
                        </linearGradient>
                    </defs>
                </svg>
            </div>

            {/* Top Banner - Fixed */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-600 text-white py-4">
                {/* Animasyonlu arka plan */}
                <div className="absolute inset-0 opacity-20 overflow-hidden">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptLTI0IDBjMy4zMTQgMCA2IDIuNjg2IDYgNnMtMi42ODYgNi02IDYtNi0yLjY4Ni02LTYgMi42ODYtNiA2LTZ6IiBmaWxsPSIjZmZmIiBvcGFjaXR5PSIuMSIvPjwvZz48L3N2Zz4=')]"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10 flex items-center justify-center">
                    {/* Ortadaki yazı */}
                    <div className="flex items-center space-x-3 animate-pulse text-center">
                        <Sparkles className="h-5 w-5" />
                        <p className="text-sm sm:text-base font-medium">
                            {t('landing.banner')}
                        </p>
                        <Sparkles className="h-5 w-5" />
                    </div>

                    {/* Sağdaki butonlar */}
                    <div className="absolute right-4 sm:right-6 flex items-center space-x-2">
                        {/* Dil seçiciye ekstra z-index */}
                        <div className="relative z-[60]">
                            <LanguageSelector />
                        </div>

                        <button
                            onClick={() => setShowAuthModal(true)}
                            className="px-4 sm:px-6 py-2 bg-white text-purple-600 rounded-lg font-semibold text-xs sm:text-sm hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap"
                        >
                            {t('landing.loginRegister')}
                        </button>
                    </div>
                </div>
            </div>





            {/* Hero Section */}
            <div className="container mx-auto px-4 py-12 sm:py-20 relative z-10 mt-16 sm:mt-20 md:mt-24 lg:mt-28">
                {/* Logo ve Başlık */}
                <div className="text-center mb-12 sm:mb-16">
                    <div className="flex justify-center items-center mb-8">
                        <img
                            src="/logo.png"
                            alt="Removvie Logo"
                            className="h-32 sm:h-40 md:h-48 w-auto"
                        />
                    </div>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        {t('landing.subtitle')}
                    </p>
                </div>

                {/* Özellikler Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-16 max-w-4xl mx-auto">
                    {/* Özellik 1 */}
                    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl">
                            <Sparkles className="h-8 w-8 text-white ml-6 mt-6 transition-all duration-300 group-hover:h-9 group-hover:w-9 group-hover:rotate-12" />
                        </div>
                        <div className="pt-12">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300 group-hover:text-purple-600 dark:group-hover:text-purple-400">
                                {t('landing.feature1Title')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t('landing.feature1Desc')}
                            </p>
                        </div>
                    </div>

                    {/* Özellik 2 */}
                    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-pink-500 to-red-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl">
                            <Heart className="h-8 w-8 text-white ml-6 mt-6 transition-all duration-300 group-hover:h-9 group-hover:w-9 group-hover:-rotate-12" />
                        </div>
                        <div className="pt-12">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300 group-hover:text-pink-600 dark:group-hover:text-pink-400">
                                {t('landing.feature2Title')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t('landing.feature2Desc')}
                            </p>
                        </div>
                    </div>

                    {/* Özellik 3 */}
                    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl">
                            <Film className="h-8 w-8 text-white ml-6 mt-6 transition-all duration-300 group-hover:h-9 group-hover:w-9 group-hover:-rotate-6" />
                        </div>
                        <div className="pt-12">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300 group-hover:text-orange-600 dark:group-hover:text-orange-400">
                                {t('landing.feature3Title')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t('landing.feature3Desc')}
                            </p>
                        </div>
                    </div>

                    {/* Özellik 4 */}
                    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                        <div className="absolute -top-6 -left-6 w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl">
                            <Star className="h-8 w-8 text-white ml-6 mt-6 transition-all duration-300 group-hover:h-9 group-hover:w-9 group-hover:rotate-12" />
                        </div>
                        <div className="pt-12">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                                {t('landing.feature4Title')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300">
                                {t('landing.feature4Desc')}
                            </p>
                        </div>
                    </div>

                </div>

                {/* Nasıl Çalışır */}
                <div className="relative bg-white dark:bg-gray-800 rounded-3xl p-8 sm:p-12 shadow-2xl mb-16 overflow-hidden">
                    {/* Dekoratif Arka Plan Şekilleri */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        {/* Sol üst köşe */}
                        <div className="absolute -top-20 -left-20 w-40 h-40 bg-purple-100 dark:bg-purple-900/20 rounded-full blur-3xl opacity-60"></div>
                        <div className="absolute top-10 left-10 w-32 h-32 border-4 border-purple-200 dark:border-purple-700 rounded-full opacity-30"></div>

                        {/* Sağ üst */}
                        <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-100 dark:bg-indigo-900/20 rounded-full blur-2xl opacity-50"></div>
                        <div className="absolute top-20 right-20 w-24 h-24 border-4 border-indigo-200 dark:border-indigo-700 rounded-lg rotate-45 opacity-20"></div>

                        {/* Sol alt */}
                        <div className="absolute -bottom-16 -left-16 w-48 h-48 bg-pink-100 dark:bg-pink-900/20 rounded-full blur-3xl opacity-40"></div>
                        <div className="absolute bottom-32 left-32 w-20 h-20 border-4 border-pink-200 dark:border-pink-700 rounded-full opacity-25"></div>

                        {/* Sağ alt */}
                        <div className="absolute -bottom-10 -right-10 w-36 h-36 bg-blue-100 dark:bg-blue-900/20 rounded-full blur-2xl opacity-50"></div>

                        {/* Ortada dalgalı çizgiler */}
                        <svg className="absolute top-1/2 left-0 w-full h-32 opacity-10" viewBox="0 0 1200 120" preserveAspectRatio="none">
                            <path d="M0,60 Q300,10 600,60 T1200,60" fill="none" stroke="url(#gradient1)" strokeWidth="3" />
                            <path d="M0,80 Q300,30 600,80 T1200,80" fill="none" stroke="url(#gradient2)" strokeWidth="2" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#9333ea" stopOpacity="0.5" />
                                    <stop offset="50%" stopColor="#6366f1" stopOpacity="0.7" />
                                    <stop offset="100%" stopColor="#9333ea" stopOpacity="0.5" />
                                </linearGradient>
                                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor="#ec4899" stopOpacity="0.3" />
                                    <stop offset="50%" stopColor="#8b5cf6" stopOpacity="0.5" />
                                    <stop offset="100%" stopColor="#ec4899" stopOpacity="0.3" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    <div className="relative z-10">
                        <h2 className="text-3xl sm:text-4xl font-bold text-center text-gray-900 dark:text-white mb-12">
                            {t('landing.howItWorks')}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
                            {/* Bağlantı çizgileri - sadece desktop'ta görünür */}
                            <div className="hidden md:block absolute top-8 left-0 right-0 h-0.5">
                                <div className="absolute left-[16.666%] right-[16.666%] h-full bg-gradient-to-r from-purple-300 via-indigo-300 to-purple-300 dark:from-purple-700 dark:via-indigo-700 dark:to-purple-700"></div>
                            </div>

                            <div className="text-center relative">
                                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg relative z-10 ring-4 ring-purple-100 dark:ring-purple-900/50">
                                    1
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                    {t('landing.step1Title')}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    {t('landing.step1Desc')}
                                </p>
                            </div>
                            <div className="text-center relative">
                                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg relative z-10 ring-4 ring-indigo-100 dark:ring-indigo-900/50">
                                    2
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                    {t('landing.step2Title')}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    {t('landing.step2Desc')}
                                </p>
                            </div>
                            <div className="text-center relative">
                                <div className="bg-gradient-to-br from-purple-500 to-indigo-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold shadow-lg relative z-10 ring-4 ring-pink-100 dark:ring-pink-900/50">
                                    3
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                                    {t('landing.step3Title')}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300">
                                    {t('landing.step3Desc')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Final CTA */}
                <div className="relative text-center bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl p-8 sm:p-12 shadow-2xl overflow-hidden">
                    {/* Soldan sağa akan beyaz ışık animasyonu */}
                    <div className="absolute inset-0 -translate-x-full animate-shimmer">
                        <div className="h-full w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12"></div>
                    </div>

                    <div className="relative z-10">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            {t('landing.ctaTitle')}
                        </h2>
                        <p className="text-xl text-purple-100 mb-8 max-w-2xl mx-auto">
                            {t('landing.ctaDesc')}
                        </p>
                        <button
                            onClick={() => setShowAuthModal(true)}
                            className="group px-10 py-5 bg-white text-purple-600 rounded-xl font-bold text-xl hover:shadow-2xl transition-all transform hover:scale-105 flex items-center space-x-2 mx-auto"
                        >
                            <span>{t('landing.ctaButton')}</span>
                            <ArrowRight className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="text-center py-8 text-gray-600 dark:text-gray-400">
                <p className="font-semibold mb-2">{t('landing.footerCopyright')}</p>
                <p className="text-xs">{t('landing.footerPowered')}</p>
            </footer>

            {/* Auth Modal */}
            {showAuthModal && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                    onClick={() => setShowAuthModal(false)}
                >
                    <div
                        className="bg-white dark:bg-gray-800 rounded-3xl max-w-md w-full shadow-2xl transform transition-all"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="relative p-8 border-b border-gray-200 dark:border-gray-700">
                            <div className="flex justify-center items-center mb-6">
                                <img
                                    src="/logo.png"
                                    alt="Removvie Logo"
                                    className="h-24 sm:h-28 w-auto"
                                />
                            </div>
                            <h2 className="text-xl text-center text-gray-900 dark:text-white font-semibold">
                                {isLogin ? t('auth.loginTitle') : t('auth.registerTitle')}
                            </h2>
                            <button
                                onClick={() => setShowAuthModal(false)}
                                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                                <X className="h-6 w-6" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-6">
                            <form className="space-y-4" onSubmit={handleSubmit}>
                                {errors.general && (
                                    <div
                                        data-error-message
                                        className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-700 text-red-600 dark:text-red-400 px-4 py-3 rounded-xl shadow-lg animate-in fade-in slide-in-from-top-2 duration-300"
                                    >
                                        <div className="flex items-center space-x-2">
                                            <svg className="h-5 w-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                            </svg>
                                            <p className="font-medium text-sm">{errors.general}</p>
                                        </div>
                                    </div>
                                )}

                                {!isLogin && (
                                    <div>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                                <User className="h-5 w-5 text-purple-500" />
                                            </div>
                                            <input
                                                name="name"
                                                type="text"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                className={`appearance-none rounded-xl relative block w-full px-4 py-3 pl-11 border ${errors.name ? 'border-red-300' : 'border-gray-300'} dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                                                placeholder={t('auth.namePlaceholder')}
                                            />
                                        </div>
                                        {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
                                    </div>
                                )}

                                {!isLogin && (
                                    <div>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                                <Calendar className="h-5 w-5 text-purple-500" />
                                            </div>
                                            <input
                                                name="birthDate"
                                                type="date"
                                                value={formData.birthDate}
                                                onChange={handleInputChange}
                                                max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                                                className={`appearance-none rounded-xl relative block w-full px-4 py-3 pl-11 border ${errors.birthDate ? 'border-red-300' : 'border-gray-300'} dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                                            />
                                        </div>
                                        {errors.birthDate && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.birthDate}</p>}
                                    </div>
                                )}

                                <div>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                            <Mail className="h-5 w-5 text-purple-500" />
                                        </div>
                                        <input
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            className={`appearance-none rounded-xl relative block w-full px-4 py-3 pl-11 border ${errors.email ? 'border-red-300' : 'border-gray-300'} dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                                            placeholder={t('auth.emailPlaceholder')}
                                        />
                                    </div>
                                    {errors.email && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>}
                                </div>

                                <div>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                            <Lock className="h-5 w-5 text-purple-500" />
                                        </div>
                                        <input
                                            name="password"
                                            type={!isLogin ? 'password' : (showPassword ? 'text' : 'password')}
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`appearance-none rounded-xl relative block w-full px-4 py-3 ${!isLogin ? 'pl-11' : 'pl-11 pr-12'} border ${errors.password ? 'border-red-300' : 'border-gray-300'} dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                                            placeholder={t('auth.passwordPlaceholder')}
                                        />
                                        {isLogin && (
                                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center z-10">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    className="text-purple-500 hover:text-purple-600 focus:outline-none"
                                                >
                                                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {errors.password && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>}
                                </div>

                                {!isLogin && (
                                    <div>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                                                <Lock className="h-5 w-5 text-purple-500" />
                                            </div>
                                            <input
                                                name="confirmPassword"
                                                type="password"
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`appearance-none rounded-xl relative block w-full px-4 py-3 pl-11 border ${errors.confirmPassword ? 'border-red-300' : 'border-gray-300'} dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
                                                placeholder={t('auth.confirmPasswordPlaceholder')}
                                            />
                                        </div>
                                        {errors.confirmPassword && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmPassword}</p>}
                                    </div>
                                )}

                                <div>
                                    <button
                                        type="submit"
                                        disabled={isLoading || showSuccess}
                                        className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        {showSuccess ? (
                                            <>
                                                <Check className="h-5 w-5 mr-2" />
                                                {isLogin ? t('auth.loginSuccess') : t('auth.registerSuccess')}
                                            </>
                                        ) : isLoading ? (
                                            t('auth.processing')
                                        ) : (
                                            isLogin ? t('auth.login') : t('auth.register')
                                        )}
                                    </button>
                                </div>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={toggleAuthMode}
                                        className="text-purple-600 hover:text-purple-500 dark:text-purple-400 dark:hover:text-purple-300 text-sm font-medium"
                                    >
                                        {isLogin ? t('auth.dontHaveAccount') : t('auth.haveAccount')}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default LandingPage;

